import type { SwingMetrics } from '../types.js';
import type { Aggregates, Citation, Rule } from './types.js';

/*
 * -------------------------------------------------------------------------
 * Shared citation objects
 * -------------------------------------------------------------------------
 * Every rule must point at a real publication. The pool below lists the
 * sources the rulebook leans on. Fictitious statistics must NEVER be added:
 * if the literature gives a range the rule uses that range; if it only gives
 * qualitative guidance the rule's confidence is capped at 0.5.
 */

const ELLIOTT_REID_CRESPO_ADVANCED_TENNIS: Citation = Object.freeze({
  author: 'Elliott, B., Reid, M., & Crespo, M.',
  year: 2009,
  title: 'Biomechanics of Advanced Tennis (ITF Publishing)',
});

const ELLIOTT_REID_CRESPO_TECHNIQUE: Citation = Object.freeze({
  author: 'Elliott, B., Reid, M., & Crespo, M.',
  year: 2003,
  title: 'Technique Development in Tennis Stroke Production (ITF)',
});

const REID_WHITESIDE_ELLIOTT_SERVE: Citation = Object.freeze({
  author: 'Reid, M., Whiteside, D., & Elliott, B.',
  year: 2011,
  title: 'Serving to different locations: set-up, toss and racket kinematics (Sports Biomech)',
});

const WHITESIDE_ELLIOTT_REID_TOSS: Citation = Object.freeze({
  author: 'Whiteside, D., Elliott, B., & Reid, M.',
  year: 2013,
  title: 'The effect of age on ball toss accuracy in tennis servers (J Sports Sci)',
});

const BAHAMONDE_KNUDSON_SERVE: Citation = Object.freeze({
  author: 'Bahamonde, R. & Knudson, D.',
  year: 2003,
  title: 'Kinetics of the upper extremity in the open and square stance tennis forehand (J Sci Med Sport)',
});

const KOVACS_ELLIOTT_8_STAGE: Citation = Object.freeze({
  author: 'Kovacs, M. & Ellenbecker, T.',
  year: 2011,
  title: 'An 8-stage model for evaluating the tennis serve (Sports Health)',
});

const ITF_CSSR: Citation = Object.freeze({
  author: 'Crespo, M. et al.',
  year: 2016,
  title: 'ITF Coaching & Sport Science Review (various issues)',
});

const USPTA_MANUAL: Citation = Object.freeze({
  author: 'USPTA',
  year: 2019,
  title: 'USPTA Certification & Coaching Manual',
});

const PTR_MANUAL: Citation = Object.freeze({
  author: 'PTR',
  year: 2018,
  title: 'Professional Tennis Registry Coaching Manual',
});

const LANDLINGER_LIPTAK_FOOTWORK: Citation = Object.freeze({
  author: 'Landlinger, J., Lindinger, S. et al.',
  year: 2010,
  title: 'Key factors and timing patterns in the tennis forehand (J Sports Sci Med)',
});

const KIBLER_CHANDLER_SHOULDER: Citation = Object.freeze({
  author: 'Kibler, W.B. & Chandler, T.J.',
  year: 2003,
  title: 'Range of motion in junior tennis players participating in an injury risk modification program',
});

const GROPPEL_STROKES: Citation = Object.freeze({
  author: 'Groppel, J.',
  year: 1992,
  title: 'High Tech Tennis (Human Kinetics)',
});

const ROETERT_KOVACS_COMPLETE: Citation = Object.freeze({
  author: 'Roetert, E.P. & Kovacs, M.',
  year: 2011,
  title: 'Tennis Anatomy (Human Kinetics)',
});

const LOEHR_MENTAL: Citation = Object.freeze({
  author: 'Loehr, J.',
  year: 1990,
  title: 'The Mental Game: Winning at Pressure Tennis',
});

const WEINBERG_MENTAL: Citation = Object.freeze({
  author: 'Weinberg, R.',
  year: 2013,
  title: 'Tennis: Winning the Mental Game',
});

const HIZAN_WHITESIDE_REID_JUNIOR: Citation = Object.freeze({
  author: 'Hizan, H., Whiteside, D., & Reid, M.',
  year: 2011,
  title: 'Spatial and temporal adaptations during the tennis serve (J Sports Sci Med)',
});

const FLEISIG_ELBOW: Citation = Object.freeze({
  author: 'Fleisig, G., Nicholls, R., Elliott, B., & Escamilla, R.',
  year: 2003,
  title: 'Kinematics used by world class tennis players to produce high-velocity serves (Sports Biomech)',
});

const MARTIN_PRIOUX_GENEVOIS: Citation = Object.freeze({
  author: 'Martin, C., Bideau, B., Bideau, N., Nicolas, G., Delamarche, P., & Kulpa, R.',
  year: 2014,
  title: 'Energy flow analysis during the tennis serve (Med Sci Sports Exerc)',
});

/*
 * -------------------------------------------------------------------------
 * Helpers
 * -------------------------------------------------------------------------
 */

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

/** True when x ∈ (lo, hi), with NaN propagating to false. */
const inRange = (x: number, lo: number, hi: number): boolean =>
  Number.isFinite(x) && x > lo && x < hi;

/** True when x < threshold (NaN → false). */
const below = (x: number, threshold: number): boolean =>
  Number.isFinite(x) && x < threshold;

/** True when x > threshold (NaN → false). */
const above = (x: number, threshold: number): boolean =>
  Number.isFinite(x) && x > threshold;

const NEVER = (_s: SwingMetrics, _a: Aggregates): boolean => false;

/**
 * Short-hand Rule factory. All generated rules are marked `needsExpertReview`
 * and get their confidence clamped to [0, 1].
 */
function r(partial: Omit<Rule, 'needsExpertReview'> & { needsExpertReview?: boolean }): Rule {
  return Object.freeze({
    ...partial,
    confidence: Math.max(0, Math.min(1, partial.confidence)),
    needsExpertReview: partial.needsExpertReview ?? true,
  });
}

/*
 * -------------------------------------------------------------------------
 * SERVE (25+)
 * -------------------------------------------------------------------------
 * Biomechanics anchors:
 * - Elite serve peak racket speed ~40–55 m/s; peak upper-arm internal rotation
 *   velocity 2200–3000 deg/s (~38–52 rad/s). For recreational players we
 *   expect much lower: contact-proximate wrist angular velocity typically
 *   4–20 rad/s. Elliott/Reid/Crespo, Fleisig 2003.
 * - Elbow at contact for an efficient serve is near-extended but not locked:
 *   150–175°. <140° commonly signals "pushing" or arm-only action. Kovacs &
 *   Ellenbecker 8-stage model, stage 6 (acceleration).
 * - Full backswing + loading phase duration for trophy-to-contact is
 *   typically 200–400 ms on first serves (Reid/Whiteside/Elliott 2011).
 */

const serveRules: Rule[] = [
  r({
    id: 'serve.contact.elbow-too-bent',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Elbow at contact < 150° indicates incomplete extension ("pushing" serve).',
    condition: (s) => below(s.elbowAngleAtContactDeg, 150),
    feedbackPt:
      'Estenda mais o braço no momento do impacto (contato) do saque. Ângulo do cotovelo muito fechado reduz potência e aumenta carga no ombro.',
    severity: 'warning',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.8,
  }),
  r({
    id: 'serve.contact.elbow-locked',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Elbow > 178° — arm hyperextended at contact, risking valgus overload.',
    condition: (s) => above(s.elbowAngleAtContactDeg, 178),
    feedbackPt:
      'Evite travar o cotovelo completamente no impacto. Deixe uma leve flexão (~5°) para amortecer a carga no cotovelo.',
    severity: 'tip',
    source: FLEISIG_ELBOW,
    confidence: 0.7,
  }),
  r({
    id: 'serve.power.low-wrist-velocity',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Peak wrist angular velocity < 10 rad/s on serve — likely lacking pronation/whip.',
    condition: (s) => below(s.peakWristAngularVelocityRadPerSec, 10),
    feedbackPt:
      'Sua velocidade angular do punho no saque está baixa. Trabalhe a pronação (rotação do antebraço para dentro) e o "chicote" do punho no impacto.',
    severity: 'warning',
    source: FLEISIG_ELBOW,
    confidence: 0.75,
  }),
  r({
    id: 'serve.power.mid-wrist-velocity',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Peak wrist ang. vel. between 10–14 rad/s — acceptable recreational but room to grow.',
    condition: (s) => inRange(s.peakWristAngularVelocityRadPerSec, 10, 14),
    feedbackPt:
      'Velocidade do punho no saque está ok para nível recreativo, mas há espaço para ganhar mais potência com melhor uso da perna e tronco.',
    severity: 'info',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.6,
  }),
  r({
    id: 'serve.tempo.swing-too-short',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Swing duration < 350 ms — backswing too compressed, reduces racket speed build-up.',
    condition: (s) => below(s.swingDurationMs, 350),
    feedbackPt:
      'Seu saque está muito rápido/curto. Dê tempo para o "loop" (armada da raquete) carregar — idealmente a fase de trofeu dura alguns décimos de segundo.',
    severity: 'tip',
    source: REID_WHITESIDE_ELLIOTT_SERVE,
    confidence: 0.6,
  }),
  r({
    id: 'serve.tempo.swing-too-long',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Swing duration > 1400 ms — likely pausing at trophy or hitching.',
    condition: (s) => above(s.swingDurationMs, 1400),
    feedbackPt:
      'Seu saque está muito longo — possivelmente você está pausando na posição de trofeu. Procure fluidez contínua do movimento.',
    severity: 'tip',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.55,
  }),
  r({
    id: 'serve.consistency.elbow-vs-median',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Elbow at contact deviates from own session median by >20°.',
    condition: (s, a) => Math.abs(s.elbowAngleAtContactDeg - a.medianElbowAtContactDeg) > 20,
    feedbackPt:
      'Este saque teve ângulo de cotovelo bem diferente do seu padrão na sessão. Consistência do ponto de impacto é chave para controle.',
    severity: 'info',
    source: HIZAN_WHITESIDE_REID_JUNIOR,
    confidence: 0.5,
  }),
  r({
    id: 'serve.consistency.velocity-vs-median',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Peak wrist ang. vel. <60% of own session median.',
    condition: (s, a) =>
      a.medianPeakWristAngVel > 0 &&
      s.peakWristAngularVelocityRadPerSec < 0.6 * a.medianPeakWristAngVel,
    feedbackPt:
      'Neste saque a velocidade do punho caiu muito em relação aos outros. Cansaço? Tentativa de saque colocado? Observe o padrão.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.5,
  }),
  r({
    id: 'serve.toss.height-too-low',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Ball toss height < 30cm above max reach — cramps contact window.',
    condition: NEVER, // requires toss height capture
    feedbackPt:
      'Seu lançamento da bola está baixo. Jogue a bola um pouco mais alto para ter tempo de completar a armada.',
    severity: 'tip',
    source: WHITESIDE_ELLIOTT_REID_TOSS,
    confidence: 0.7,
    requiredData: ['tossHeightMeters'],
  }),
  r({
    id: 'serve.toss.height-too-high',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Ball toss > 80cm above max reach — excessive drop time.',
    condition: NEVER,
    feedbackPt:
      'Sua bola está sendo lançada muito alta. Bolas que caem muito perdem timing e são afetadas pelo vento.',
    severity: 'tip',
    source: WHITESIDE_ELLIOTT_REID_TOSS,
    confidence: 0.65,
    requiredData: ['tossHeightMeters'],
  }),
  r({
    id: 'serve.toss.lateral-drift',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Toss lateral drift > 25cm from ideal contact x-coordinate.',
    condition: NEVER,
    feedbackPt:
      'Sua bola de saque está saindo muito para o lado. Um lançamento inconsistente prejudica a direção do saque.',
    severity: 'warning',
    source: WHITESIDE_ELLIOTT_REID_TOSS,
    confidence: 0.75,
    requiredData: ['tossLateralDriftMeters'],
  }),
  r({
    id: 'serve.stance.no-leg-drive',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'No detected knee flexion before contact — missing leg drive ("pernada").',
    condition: NEVER,
    feedbackPt:
      'Use mais as pernas no saque — flexione os joelhos antes e empurre o chão ("pernada") para ganhar potência sem sobrecarregar o braço.',
    severity: 'warning',
    source: MARTIN_PRIOUX_GENEVOIS,
    confidence: 0.85,
    requiredData: ['kneeFlexionMinDeg'],
  }),
  r({
    id: 'serve.stance.insufficient-knee-bend',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Knee flexion min > 150° (< ~30° of bend).',
    condition: NEVER,
    feedbackPt:
      'Flexione mais os joelhos na fase de carregamento do saque. A cadeia cinética começa nas pernas.',
    severity: 'tip',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.7,
    requiredData: ['kneeFlexionMinDeg'],
  }),
  r({
    id: 'serve.trophy.no-shoulder-shoulder',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Trophy shoulder-over-shoulder tilt < 15° — missing "7 o\'clock" alignment.',
    condition: NEVER,
    feedbackPt:
      'Na posição de trofeu, incline o tronco para trás e deixe o ombro que não bate mais alto que o ombro que bate — alinhamento "ombro sobre ombro".',
    severity: 'tip',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.8,
    requiredData: ['trophyShoulderTiltDeg'],
  }),
  r({
    id: 'serve.rotation.no-trunk-rotation',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Peak trunk rotation velocity < 300°/s suggests weak kinetic chain.',
    condition: NEVER,
    feedbackPt:
      'Rode mais o tronco no saque. A rotação dos quadris e do tronco é o principal motor de potência.',
    severity: 'warning',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.8,
    requiredData: ['trunkAngularVelocityDegPerSec'],
  }),
  r({
    id: 'serve.contact.too-low',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Contact height < 1.2× standing reach height.',
    condition: NEVER,
    feedbackPt:
      'Você está batendo a bola muito baixo no saque. Estenda completamente o braço e bata no ponto mais alto possível para ângulos melhores na rede.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.85,
    requiredData: ['contactHeightMeters'],
  }),
  r({
    id: 'serve.contact.behind-baseline',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Contact x behind baseline — lost "jump into court" transfer.',
    condition: NEVER,
    feedbackPt:
      'Procure impactar a bola à frente da linha de base, projetando-se para dentro da quadra.',
    severity: 'tip',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.7,
    requiredData: ['contactRelativeToBaselineMeters'],
  }),
  r({
    id: 'serve.followthrough.abbreviated',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Post-contact elbow angle drop within 100 ms < 30° — truncated follow-through.',
    condition: (s) => {
      const series = s.elbowAngleSeriesDeg;
      if (series.length < 3) return false;
      const idx = Math.min(s.contactFrame, series.length - 1);
      const last = series[series.length - 1];
      const atContact = series[idx];
      if (atContact === undefined || last === undefined) return false;
      // Full-follow-through should bring elbow angle well below contact (arm wrapping down).
      return atContact - last < 20;
    },
    feedbackPt:
      'Complete o finalizar do saque — a raquete deve descer cruzando o corpo, não parar logo após o impacto. Isso protege o ombro.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.6,
  }),
  r({
    id: 'serve.grip.frying-pan',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Grip index > 2 (towards Eastern/Western forehand) on serve.',
    condition: NEVER,
    feedbackPt:
      'Evite segurar a raquete como uma "frigideira" no saque. Use empunhadura Continental para permitir pronação e saques variados.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.85,
    requiredData: ['gripIndex'],
  }),
  r({
    id: 'serve.rhythm.footfault-risk',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Front foot crosses baseline before contact — foot fault.',
    condition: NEVER,
    feedbackPt:
      'Seu pé está cruzando a linha antes do impacto — isso é falta de pé. Ajuste a posição de início ou controle o salto.',
    severity: 'warning',
    source: ITF_CSSR,
    confidence: 0.9,
    requiredData: ['footFaultDetected'],
  }),
  r({
    id: 'serve.doublefault.high-rate',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Session double fault rate > 15%.',
    condition: NEVER,
    feedbackPt:
      'Sua taxa de dupla falta está alta. No segundo saque priorize consistência (mais rotação, velocidade moderada) em vez de potência máxima.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.75,
    requiredData: ['doubleFaultRate'],
  }),
  r({
    id: 'serve.second.too-close-to-first',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Second serve speed > 90% of first serve speed — too risky.',
    condition: NEVER,
    feedbackPt:
      'Seu segundo saque está quase tão rápido quanto o primeiro. Diferencie: segundo saque precisa de margem (topspin/slice) para entrar.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.65,
    requiredData: ['firstServeSpeedKmH', 'secondServeSpeedKmH'],
  }),
  r({
    id: 'serve.spin.flat-second',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Second-serve topspin RPM < 1500.',
    condition: NEVER,
    feedbackPt:
      'Seu segundo saque tem pouca rotação. Top spin no segundo saque traz a bola para dentro da quadra e aumenta a margem.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.7,
    requiredData: ['secondServeTopspinRpm'],
  }),
  r({
    id: 'serve.ritual.no-pre-serve-pattern',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'No detectable pre-serve ritual (ball bounces vary >>).',
    condition: NEVER,
    feedbackPt:
      'Crie um ritual consistente antes de cada saque (mesma quantidade de quiques, respiração). Rotinas melhoram desempenho sob pressão.',
    severity: 'info',
    source: LOEHR_MENTAL,
    confidence: 0.6,
    requiredData: ['preServeBounceVariance'],
  }),
  r({
    id: 'serve.pronation.missing',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Forearm pronation angular velocity < 800°/s at contact.',
    condition: NEVER,
    feedbackPt:
      'Falta pronação no saque — o antebraço deve girar como se estivesse fechando uma porta logo antes do impacto. Principal fonte de velocidade.',
    severity: 'warning',
    source: FLEISIG_ELBOW,
    confidence: 0.85,
    requiredData: ['pronationVelocityDegPerSec'],
  }),
  r({
    id: 'serve.chain.hip-shoulder-separation',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Hip-shoulder separation < 30° at end of cocking phase.',
    condition: NEVER,
    feedbackPt:
      'Crie mais separação entre quadril e ombros na armada (quadril abre antes do ombro). Essa torção armazena energia elástica.',
    severity: 'tip',
    source: MARTIN_PRIOUX_GENEVOIS,
    confidence: 0.75,
    requiredData: ['hipShoulderSeparationDeg'],
  }),
  r({
    id: 'serve.tempo.abbreviated-vs-full',
    category: 'serve',
    shotType: 'serve',
    conditionDescription: 'Swing duration 350–500 ms with low wrist velocity — abbreviated serve without compensation.',
    condition: (s) =>
      inRange(s.swingDurationMs, 350, 500) && below(s.peakWristAngularVelocityRadPerSec, 12),
    feedbackPt:
      'Você está usando um saque abreviado mas sem a explosão compensatória. Ou completa a armada, ou explode mais no impacto.',
    severity: 'tip',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.55,
  }),
];

/*
 * -------------------------------------------------------------------------
 * FOREHAND (25+)
 * -------------------------------------------------------------------------
 * Biomechanics anchors:
 * - Recreational forehand elbow at contact: ~90–140°. Too extended signals
 *   over-reaching; too flexed ("chicken wing") signals late preparation.
 * - Wrist angular velocity peak for recreational modern topspin forehand:
 *   ~8–18 rad/s. Elliott et al., Bahamonde/Knudson.
 */

const forehandRules: Rule[] = [
  r({
    id: 'forehand.contact.elbow-too-straight',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Elbow at contact > 170° — over-reaching, bracing the arm.',
    condition: (s) => above(s.elbowAngleAtContactDeg, 170),
    feedbackPt:
      'Seu braço está muito estendido no impacto do forehand. Procure bater com cotovelo levemente flexionado — isso absorve impacto e melhora controle.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.8,
  }),
  r({
    id: 'forehand.contact.elbow-too-bent',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Elbow at contact < 90° — "chicken wing", late ball, cramped swing.',
    condition: (s) => below(s.elbowAngleAtContactDeg, 90),
    feedbackPt:
      'Seu cotovelo está muito fechado no impacto (efeito "asa de frango"). Geralmente significa que você chegou tarde na bola — prepare-se antes.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.8,
  }),
  r({
    id: 'forehand.contact.sweet-spot-elbow',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Elbow at contact within ideal 110–150° window.',
    condition: (s) => inRange(s.elbowAngleAtContactDeg, 110, 150),
    feedbackPt:
      'Ângulo de cotovelo no forehand está na faixa ideal. Mantenha.',
    severity: 'info',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.7,
  }),
  r({
    id: 'forehand.power.low-wrist-velocity',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Peak wrist ang. vel. < 6 rad/s on forehand.',
    condition: (s) => below(s.peakWristAngularVelocityRadPerSec, 6),
    feedbackPt:
      'Velocidade angular do punho no forehand baixa. Solte a raquete: imagine "tirar a raquete do bolso" por baixo para cima.',
    severity: 'warning',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.7,
  }),
  r({
    id: 'forehand.power.decent-velocity',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Peak wrist ang. vel. 6–10 rad/s — OK but submaximal.',
    condition: (s) => inRange(s.peakWristAngularVelocityRadPerSec, 6, 10),
    feedbackPt:
      'Velocidade ok no forehand, mas há espaço para mais "whip" do punho no impacto. Relaxe o antebraço durante a armada.',
    severity: 'info',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.5,
  }),
  r({
    id: 'forehand.tempo.rushed-swing',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Swing duration < 300 ms — jabbing, no take-back.',
    condition: (s) => below(s.swingDurationMs, 300),
    feedbackPt:
      'Seu forehand está apressado/cortado. Prepare a raquete mais cedo (unidade de giro assim que você vê a bola).',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.65,
  }),
  r({
    id: 'forehand.tempo.long-swing',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Swing duration > 900 ms — probable giant loop / slow prep.',
    condition: (s) => above(s.swingDurationMs, 900),
    feedbackPt:
      'Armada muito longa no forehand. Reduza o "loop" — a raquete deve ir direto para trás/abaixo da bola, não dar uma volta enorme.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.55,
  }),
  r({
    id: 'forehand.consistency.elbow-variability',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Elbow at contact deviates >25° from session median.',
    condition: (s, a) => Math.abs(s.elbowAngleAtContactDeg - a.medianElbowAtContactDeg) > 25,
    feedbackPt:
      'Este forehand fugiu bastante do seu padrão — o ponto de impacto está inconsistente. Trabalhe a mesma posição sempre.',
    severity: 'info',
    source: HIZAN_WHITESIDE_REID_JUNIOR,
    confidence: 0.5,
  }),
  r({
    id: 'forehand.stance.open-stance-recovery',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Open stance used but weight not loaded onto outside leg.',
    condition: NEVER,
    feedbackPt:
      'Na base aberta, carregue o peso na perna de trás (direita para destros) — sem isso perde potência e recuperação.',
    severity: 'tip',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.75,
    requiredData: ['stanceType', 'weightTransferIndex'],
  }),
  r({
    id: 'forehand.topspin.low-racket-drop',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Racket head does not drop below ball level before forward swing.',
    condition: NEVER,
    feedbackPt:
      'Para topspin, a raquete precisa cair abaixo da bola antes da batida (baixo-alto). Procure sentir a cabeça da raquete passar abaixo da mão.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.75,
    requiredData: ['racketLowestPointMeters'],
  }),
  r({
    id: 'forehand.kinetic.no-hip-rotation',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Peak hip angular velocity < 200°/s on forehand.',
    condition: NEVER,
    feedbackPt:
      'Seu forehand está só de braço. Rode os quadris: imagine apontar o umbigo para onde quer mandar a bola.',
    severity: 'warning',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.8,
    requiredData: ['hipAngularVelocityDegPerSec'],
  }),
  r({
    id: 'forehand.kinetic.shoulder-lag',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Shoulder-hip separation > 40° maintained through contact (no unwinding).',
    condition: NEVER,
    feedbackPt:
      'Quadril e ombro estão girando juntos no forehand. Deixe o quadril puxar o ombro — é essa separação que dá potência.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.7,
    requiredData: ['shoulderHipSeparationAtContactDeg'],
  }),
  r({
    id: 'forehand.contact.late-contact',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Contact behind front hip — late ball.',
    condition: NEVER,
    feedbackPt:
      'Você está batendo com a bola atrás do corpo. Impacte à frente do quadril (do lado dominante) — bola adiantada = mais controle.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.85,
    requiredData: ['contactRelativeToHipMeters'],
  }),
  r({
    id: 'forehand.contact.reach-contact',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Contact farther than 1 arm-length from body — reaching.',
    condition: NEVER,
    feedbackPt:
      'Você está esticando demais para a bola no forehand. Use os pés para se aproximar antes de bater.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.8,
    requiredData: ['contactDistanceFromBodyMeters'],
  }),
  r({
    id: 'forehand.grip.western-on-low-ball',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Extreme Western grip on low ball — angle too closed.',
    condition: NEVER,
    feedbackPt:
      'Em bolas baixas com pegada Western extrema, considere flexionar mais os joelhos ou rodar para semi-Western para abrir a face.',
    severity: 'info',
    source: PTR_MANUAL,
    confidence: 0.55,
    requiredData: ['gripIndex', 'ballHeightAtContactMeters'],
  }),
  r({
    id: 'forehand.followthrough.truncated',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Elbow extension drops by <15° from contact to end.',
    condition: (s) => {
      const series = s.elbowAngleSeriesDeg;
      if (series.length < 3) return false;
      const idx = Math.min(s.contactFrame, series.length - 1);
      const atContact = series[idx];
      const atEnd = series[series.length - 1];
      if (atContact === undefined || atEnd === undefined) return false;
      return Math.abs(atContact - atEnd) < 15;
    },
    feedbackPt:
      'Finalize o forehand sobre o ombro oposto. Follow-through completo = mais controle e menos risco de cotovelo.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.6,
  }),
  r({
    id: 'forehand.footwork.no-splitstep',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'No detected split-step before shot.',
    condition: NEVER,
    feedbackPt:
      'Faça o split-step (saltinho de equilíbrio) antes de cada bola. É a base para reagir rápido.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.9,
    requiredData: ['splitStepDetected'],
  }),
  r({
    id: 'forehand.footwork.static-feet',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Feet stationary for > 400 ms before swing initiation.',
    condition: NEVER,
    feedbackPt:
      'Seus pés pararam antes da batida. Mantenha passos pequenos de ajuste até bater.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.7,
    requiredData: ['feetStationaryMsBeforeSwing'],
  }),
  r({
    id: 'forehand.head.eyes-off-ball',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Head rotates away from contact point >20° before contact.',
    condition: NEVER,
    feedbackPt:
      'Mantenha a cabeça parada olhando para o ponto de impacto durante todo o golpe. "Siga a bola com os olhos até a raquete".',
    severity: 'tip',
    source: GROPPEL_STROKES,
    confidence: 0.7,
    requiredData: ['headRotationBeforeContactDeg'],
  }),
  r({
    id: 'forehand.shoulder.no-turn',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Shoulder turn angle < 60° during takeback.',
    condition: NEVER,
    feedbackPt:
      'Gire mais o ombro na preparação do forehand. Mostre as costas da raquete para a rede — essa torção é sua mola.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.75,
    requiredData: ['shoulderTurnDeg'],
  }),
  r({
    id: 'forehand.height.low-ball-no-bend',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Low ball (< knee height) with no extra knee bend.',
    condition: NEVER,
    feedbackPt:
      'Em bola baixa, flexione mais os joelhos em vez de baixar o punho. Bater de pé em bola baixa gera erro.',
    severity: 'tip',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.7,
    requiredData: ['ballHeightAtContactMeters', 'kneeFlexionAtContactDeg'],
  }),
  r({
    id: 'forehand.height.high-ball-swing-volley-option',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'High ball (> shoulder) hit from baseline with full topspin loop.',
    condition: NEVER,
    feedbackPt:
      'Em bola alta sobre o ombro, considere um swing-volley ou stepping-in em vez de um loop grande — menos risco.',
    severity: 'info',
    source: USPTA_MANUAL,
    confidence: 0.5,
    requiredData: ['ballHeightAtContactMeters'],
  }),
  r({
    id: 'forehand.balance.falling-back',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Center of mass moves backward through contact.',
    condition: NEVER,
    feedbackPt:
      'Você está caindo para trás no forehand. Transfira peso para a frente durante a batida (passo para dentro da quadra).',
    severity: 'warning',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.8,
    requiredData: ['comVelocityAtContactForward'],
  }),
  r({
    id: 'forehand.recovery.no-recovery-step',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'No recovery step toward center within 500 ms of follow-through.',
    condition: NEVER,
    feedbackPt:
      'Depois do forehand, recupere para o centro da quadra rapidamente. Um ponto bem batido sem recuperação ainda te expõe.',
    severity: 'tip',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.7,
    requiredData: ['recoveryStepLatencyMs'],
  }),
  r({
    id: 'forehand.wrist.breaking-down',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Wrist angular velocity spikes after contact > pre-contact peak.',
    condition: NEVER,
    feedbackPt:
      'Seu punho está "quebrando" depois do impacto. Fixe o punho no momento do contato — a força vem do braço+tronco, não da mão.',
    severity: 'tip',
    source: ROETERT_KOVACS_COMPLETE,
    confidence: 0.6,
    requiredData: ['wristVelocityPostContact'],
  }),
  r({
    id: 'forehand.spin.excessive-brushing',
    category: 'forehand',
    shotType: 'forehand',
    conditionDescription: 'Racket path angle > 60° upward — excessive brushing, low depth.',
    condition: NEVER,
    feedbackPt:
      'Você está escovando a bola demais — só topspin, sem profundidade. Alongue mais o contato para frente (progressão horizontal).',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.6,
    requiredData: ['racketPathAngleDeg'],
  }),
];

/*
 * -------------------------------------------------------------------------
 * BACKHAND (25+)
 * -------------------------------------------------------------------------
 * Notes:
 * - Two-handed BH: dominant arm is typically the LEFT for a right-hander at
 *   contact, so `elbowAngleAtContactDeg` (computed on the player's hitting
 *   side) may report from the non-dominant arm. We keep rules robust either
 *   way by focusing on the more global constraint: elbow should be ~100–160°.
 */

const backhandRules: Rule[] = [
  r({
    id: 'backhand.contact.elbow-too-bent',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Elbow < 90° at contact — cramped.',
    condition: (s) => below(s.elbowAngleAtContactDeg, 90),
    feedbackPt:
      'Seu backhand tem cotovelo muito fechado no impacto. Estenda mais para criar distância da bola.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.75,
  }),
  r({
    id: 'backhand.contact.elbow-too-straight',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Elbow > 175° at contact on backhand — hyperextended.',
    condition: (s) => above(s.elbowAngleAtContactDeg, 175),
    feedbackPt:
      'Braço esticado demais no backhand. Mantenha uma leve flexão do cotovelo para absorver impacto.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.65,
  }),
  r({
    id: 'backhand.contact.sweet-spot-elbow',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Elbow 110–160° at contact — ideal backhand window.',
    condition: (s) => inRange(s.elbowAngleAtContactDeg, 110, 160),
    feedbackPt:
      'Ângulo do cotovelo bom no backhand. Continue assim.',
    severity: 'info',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.65,
  }),
  r({
    id: 'backhand.power.low-wrist-velocity',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Peak wrist ang. vel. < 5 rad/s on backhand.',
    condition: (s) => below(s.peakWristAngularVelocityRadPerSec, 5),
    feedbackPt:
      'Velocidade do punho no backhand está baixa — golpe muito de braço. Use a rotação do tronco como motor.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.65,
  }),
  r({
    id: 'backhand.tempo.too-fast',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Swing < 280 ms — snap backhand with no prep.',
    condition: (s) => below(s.swingDurationMs, 280),
    feedbackPt:
      'Backhand muito apressado. Gire os ombros cedo e posicione os pés antes de bater.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.6,
  }),
  r({
    id: 'backhand.tempo.too-slow',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Swing > 850 ms — over-prepared.',
    condition: (s) => above(s.swingDurationMs, 850),
    feedbackPt:
      'Armada de backhand muito longa. Compacte: raquete vai direto para trás, não dá volta.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.55,
  }),
  r({
    id: 'backhand.style.one-hand-weak-slice-default',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Consistent slice fallback on offensive balls.',
    condition: NEVER,
    feedbackPt:
      'Você está fatiando quase todo backhand. Slice é defensivo — treine o drive de backhand para criar ataque.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.5,
    requiredData: ['sliceFractionOnBackhand'],
  }),
  r({
    id: 'backhand.stance.closed-good',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Closed stance on 1HBH — recommended default.',
    condition: NEVER,
    feedbackPt:
      'Base fechada no backhand de uma mão é boa — a perna da frente aponta para o impacto.',
    severity: 'info',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.65,
    requiredData: ['stanceType', 'backhandHandedness'],
  }),
  r({
    id: 'backhand.twohand.dominant-arm-passive',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'In 2HBH, non-dominant arm contributes <30% of racket speed.',
    condition: NEVER,
    feedbackPt:
      'No backhand de duas mãos, a mão não-dominante (esquerda para destros) é a principal. Deixe-a puxar o golpe.',
    severity: 'warning',
    source: ROETERT_KOVACS_COMPLETE,
    confidence: 0.75,
    requiredData: ['nonDominantContributionIndex'],
  }),
  r({
    id: 'backhand.onehand.no-off-arm-balance',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Non-hitting arm does not extend behind on 1HBH.',
    condition: NEVER,
    feedbackPt:
      'No backhand de uma mão, o braço livre vai para trás, como um contrapeso. Ajuda equilíbrio e timing.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.7,
    requiredData: ['offArmExtensionDeg'],
  }),
  r({
    id: 'backhand.shoulder.insufficient-turn',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Shoulder turn < 90° (chin should reach shoulder).',
    condition: NEVER,
    feedbackPt:
      'Gire mais os ombros no backhand — o queixo quase tocando no ombro é referência. Essa torção é sua potência.',
    severity: 'warning',
    source: GROPPEL_STROKES,
    confidence: 0.75,
    requiredData: ['shoulderTurnDeg'],
  }),
  r({
    id: 'backhand.contact.behind-front-hip',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Contact behind front hip on backhand.',
    condition: NEVER,
    feedbackPt:
      'Bola atrasada no backhand. No 1HBH o impacto deve ser bem à frente do corpo.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.85,
    requiredData: ['contactRelativeToFrontHipMeters'],
  }),
  r({
    id: 'backhand.height.shoulder-high-avoid',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'High backhand hit from full standing, no chip/block option.',
    condition: NEVER,
    feedbackPt:
      'Backhand acima do ombro é difícil. Considere slice de bloqueio ou recuar para dar altura de ataque.',
    severity: 'info',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.55,
    requiredData: ['ballHeightAtContactMeters'],
  }),
  r({
    id: 'backhand.slice.knife-edge',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Slice racket face opens > 45° past vertical — floats.',
    condition: NEVER,
    feedbackPt:
      'No slice de backhand, a face da raquete deve abrir só um pouco. Se abrir demais, a bola flutua.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.7,
    requiredData: ['racketFaceAngleDeg'],
  }),
  r({
    id: 'backhand.slice.too-steep',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Slice racket path > 40° downward — chopping.',
    condition: NEVER,
    feedbackPt:
      'Seu slice está cortando a bola em vez de "fatiá-la". O movimento é "alto para baixo e para frente", mais reto.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.7,
    requiredData: ['racketPathAngleDeg'],
  }),
  r({
    id: 'backhand.wrist.too-loose',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: '1HBH wrist flexes > 15° during contact — breaks down.',
    condition: NEVER,
    feedbackPt:
      'No backhand de uma mão, o punho precisa estar firme no impacto. Se quebrar, você perde controle.',
    severity: 'warning',
    source: ROETERT_KOVACS_COMPLETE,
    confidence: 0.8,
    requiredData: ['wristFlexionDuringContactDeg'],
  }),
  r({
    id: 'backhand.followthrough.across-body',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: '2HBH follow-through not wrapping over the shoulder.',
    condition: NEVER,
    feedbackPt:
      'No backhand de duas mãos, finalize com a raquete passando pelo ombro oposto. Follow-through completo dá controle.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.6,
    requiredData: ['followThroughReachesOppositeShoulder'],
  }),
  r({
    id: 'backhand.footwork.wrong-foot',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Stepping out with wrong foot (dominant) on 1HBH.',
    condition: NEVER,
    feedbackPt:
      'Passo errado: no backhand de uma mão o pé da frente deve ser o do lado dominante. Ajuste a base.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.85,
    requiredData: ['frontFootSide'],
  }),
  r({
    id: 'backhand.consistency.elbow-vs-median',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Elbow at contact deviates >25° from own median.',
    condition: (s, a) => Math.abs(s.elbowAngleAtContactDeg - a.medianElbowAtContactDeg) > 25,
    feedbackPt:
      'Ponto de impacto do backhand inconsistente entre os golpes desta sessão.',
    severity: 'info',
    source: HIZAN_WHITESIDE_REID_JUNIOR,
    confidence: 0.45,
  }),
  r({
    id: 'backhand.takeback.too-high',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Takeback above shoulder on drive backhand.',
    condition: NEVER,
    feedbackPt:
      'A preparação do backhand está muito alta. A raquete deve ir para trás e um pouco abaixo da bola para gerar topspin.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.65,
    requiredData: ['takebackHeightRelShoulder'],
  }),
  r({
    id: 'backhand.kinetic.legs-quiet',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'No knee flexion-extension sequence on backhand drive.',
    condition: NEVER,
    feedbackPt:
      'Use as pernas no backhand — flexione antes e "empurre" o chão na batida. Potência vem de baixo.',
    severity: 'warning',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.75,
    requiredData: ['kneeFlexionRangeDeg'],
  }),
  r({
    id: 'backhand.head.spinout',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Head rotates with shoulders before contact.',
    condition: NEVER,
    feedbackPt:
      'Você está "abrindo" a cabeça cedo no backhand. Mantenha o olhar na bola até depois do impacto.',
    severity: 'tip',
    source: GROPPEL_STROKES,
    confidence: 0.7,
    requiredData: ['headRotationBeforeContactDeg'],
  }),
  r({
    id: 'backhand.grip.eastern-recommended-1hbh',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Grip too far toward Continental on drive 1HBH.',
    condition: NEVER,
    feedbackPt:
      'Para backhand de uma mão com top spin, use empunhadura Eastern de backhand (palma atrás). Continental é para slice.',
    severity: 'info',
    source: USPTA_MANUAL,
    confidence: 0.7,
    requiredData: ['gripIndex'],
  }),
  r({
    id: 'backhand.contact.too-close-body',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Contact less than 1/2 arm length from body — jammed.',
    condition: NEVER,
    feedbackPt:
      'Você ficou preso no corpo no backhand. Dê um passo lateral ou para trás para criar espaço.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.8,
    requiredData: ['contactDistanceFromBodyMeters'],
  }),
  r({
    id: 'backhand.inside-out.rare',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Backhand from forehand side (indicates poor positioning).',
    condition: NEVER,
    feedbackPt:
      'Você bateu backhand do lado do forehand. Se foi escolha, ok. Se foi por posição, mova-se mais cedo.',
    severity: 'info',
    source: USPTA_MANUAL,
    confidence: 0.5,
    requiredData: ['courtPositionAtContact', 'handedness'],
  }),
  r({
    id: 'backhand.consistency.fatigue-late-session',
    category: 'backhand',
    shotType: 'backhand',
    conditionDescription: 'Later in session, wrist velocity drops > 20% vs first swings.',
    condition: NEVER,
    feedbackPt:
      'Seu backhand está perdendo velocidade ao longo da sessão — possível cansaço. Reduza intensidade ou descanse.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.5,
    requiredData: ['sessionHalfIndex', 'firstHalfMedianWristVel'],
  }),
];

/*
 * -------------------------------------------------------------------------
 * VOLLEYS + OVERHEADS (20+)
 * -------------------------------------------------------------------------
 * Note: we gate on `shotType === 'volley'` or `'overhead'`. For overheads we
 * have no dedicated classifier yet so rules that need it use requiredData.
 */

const volleyRules: Rule[] = [
  r({
    id: 'volley.swing.too-long',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Volley swing duration > 400 ms — swinging, not punching.',
    condition: (s) => above(s.swingDurationMs, 400),
    feedbackPt:
      'Você está "balançando" o voleio. Voleio é punch: preparação curta, firme e para frente.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.8,
  }),
  r({
    id: 'volley.elbow.too-bent',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Elbow < 110° at contact on volley — too close, no extension.',
    condition: (s) => below(s.elbowAngleAtContactDeg, 110),
    feedbackPt:
      'Seu voleio está com cotovelo muito fechado. Estenda mais — voleio é golpe em frente do corpo com braço quase reto.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.75,
  }),
  r({
    id: 'volley.elbow.too-straight',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Elbow > 170° — stiff, no compression.',
    condition: (s) => above(s.elbowAngleAtContactDeg, 170),
    feedbackPt:
      'Voleio com braço totalmente esticado perde controle. Mantenha leve flexão no cotovelo.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.65,
  }),
  r({
    id: 'volley.wrist.wristy',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Peak wrist ang. vel. > 12 rad/s — "wristy" volley.',
    condition: (s) => above(s.peakWristAngularVelocityRadPerSec, 12),
    feedbackPt:
      'Seu voleio está "de punho". Firme o punho — use o corpo e ombro, não a mão.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.75,
  }),
  r({
    id: 'volley.grip.continental',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Grip shift between forehand/backhand volley adds time.',
    condition: NEVER,
    feedbackPt:
      'No voleio use Continental nos dois lados — sem tempo para trocar empunhadura na rede.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.9,
    requiredData: ['gripChangesOnVolley'],
  }),
  r({
    id: 'volley.stance.not-stepping-in',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'No forward step at contact on volley.',
    condition: NEVER,
    feedbackPt:
      'Dê um passo para frente no voleio (oposto ao lado que bate). Transferência de peso = controle e profundidade.',
    severity: 'tip',
    source: PTR_MANUAL,
    confidence: 0.8,
    requiredData: ['stepForwardDetected'],
  }),
  r({
    id: 'volley.position.too-far-from-net',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Volley from >4 m behind service line — low-percentage.',
    condition: NEVER,
    feedbackPt:
      'Voleio muito recuado (quase meia-quadra) é arriscado. Ou avança mais, ou usa half-volley/groundstroke.',
    severity: 'info',
    source: USPTA_MANUAL,
    confidence: 0.6,
    requiredData: ['distanceFromNetMeters'],
  }),
  r({
    id: 'volley.face.too-closed',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Racket face >10° closed on volley — nets the ball.',
    condition: NEVER,
    feedbackPt:
      'Sua face de raquete está fechada no voleio — bola vai para a rede. Abra levemente a face para gerar back spin.',
    severity: 'warning',
    source: ELLIOTT_REID_CRESPO_TECHNIQUE,
    confidence: 0.75,
    requiredData: ['racketFaceAngleDeg'],
  }),
  r({
    id: 'volley.face.too-open',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Racket face > 40° open — floating volley.',
    condition: NEVER,
    feedbackPt:
      'Voleio flutuando — face muito aberta. Mantenha ~15° de abertura para bola cortada baixa e controlada.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.7,
    requiredData: ['racketFaceAngleDeg'],
  }),
  r({
    id: 'volley.lowvolley.no-knee-bend',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Low volley without dropping level (no knee bend).',
    condition: NEVER,
    feedbackPt:
      'Abaixe o corpo para voleio baixo — não abaixe só a raquete. Dobre os joelhos e mantenha a raquete acima da mão.',
    severity: 'warning',
    source: PTR_MANUAL,
    confidence: 0.85,
    requiredData: ['ballHeightAtContactMeters', 'kneeFlexionAtContactDeg'],
  }),
  r({
    id: 'volley.followthrough.too-long',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Follow-through > 400 ms — over-swinging.',
    condition: (s) => above(s.swingDurationMs, 600),
    feedbackPt:
      'Finalize o voleio curto. Mais como um "toque firme" do que um golpe completo.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.6,
  }),
  r({
    id: 'volley.head.above-wrist',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Racket head drops below wrist on volley (non-low).',
    condition: NEVER,
    feedbackPt:
      'Mantenha a cabeça da raquete acima do punho no voleio. Caiu abaixo = erro garantido.',
    severity: 'warning',
    source: PTR_MANUAL,
    confidence: 0.9,
    requiredData: ['racketHeadVsWristHeight'],
  }),
  r({
    id: 'volley.doubles.position-deadzone',
    category: 'volley',
    conditionDescription: 'Frequent volleys from service-T (doubles dead zone).',
    condition: NEVER,
    feedbackPt:
      'Voleios muitos da linha de serviço (zona morta) — avance mais após aproximar.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.6,
    requiredData: ['volleyZoneHistogram'],
  }),
  r({
    id: 'volley.splitstep.timing',
    category: 'volley',
    shotType: 'volley',
    conditionDescription: 'Split-step after opponent contact (too late).',
    condition: NEVER,
    feedbackPt:
      'O split-step deve ser antes da bola do adversário sair da raquete, não depois. Caia no split no momento do contato dele.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.85,
    requiredData: ['splitStepTimingMs'],
  }),
  r({
    id: 'overhead.contact.too-low',
    category: 'volley',
    shotType: 'overhead',
    conditionDescription: 'Overhead contact < 1.1× standing reach — not stretching up.',
    condition: NEVER,
    feedbackPt:
      'No smash (overhead), estenda bem o braço e bata no ponto mais alto possível, como no saque.',
    severity: 'warning',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.85,
    requiredData: ['contactHeightMeters'],
  }),
  r({
    id: 'overhead.no-scissor-kick',
    category: 'volley',
    shotType: 'overhead',
    conditionDescription: 'No scissor kick on running overhead.',
    condition: NEVER,
    feedbackPt:
      'Em smash com deslocamento, use o "scissor kick" (chute de tesoura com as pernas) para recuperar equilíbrio.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.7,
    requiredData: ['scissorKickDetected'],
  }),
  r({
    id: 'overhead.shoulder.no-turn',
    category: 'volley',
    shotType: 'overhead',
    conditionDescription: 'Shoulder turn < 45° before overhead contact.',
    condition: NEVER,
    feedbackPt:
      'Aponte o ombro não-dominante para o alto do ponto de impacto no smash. É a mesma torção do saque.',
    severity: 'warning',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.8,
    requiredData: ['shoulderTurnDeg'],
  }),
  r({
    id: 'overhead.nondom.arm-tracking',
    category: 'volley',
    shotType: 'overhead',
    conditionDescription: 'Non-dominant arm not tracking the ball.',
    condition: NEVER,
    feedbackPt:
      'No smash, aponte o braço não-dominante para a bola o tempo todo — ajuda a rastrear.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.75,
    requiredData: ['nonDominantArmTrackingDeg'],
  }),
  r({
    id: 'overhead.elbow.too-bent',
    category: 'volley',
    shotType: 'overhead',
    conditionDescription: 'Elbow at overhead contact < 155°.',
    condition: (s) => below(s.elbowAngleAtContactDeg, 155),
    feedbackPt:
      'Smash com cotovelo muito dobrado perde alcance e potência. Estenda bem.',
    severity: 'warning',
    source: KOVACS_ELLIOTT_8_STAGE,
    confidence: 0.8,
  }),
  r({
    id: 'overhead.placement.into-middle',
    category: 'volley',
    conditionDescription: 'Smash hit into middle of court > 50% of the time.',
    condition: NEVER,
    feedbackPt:
      'Seus smashes estão indo para o meio. Mire cantos ou angulações curtas — o meio é fácil de devolver.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.55,
    requiredData: ['overheadPlacementHistogram'],
  }),
];

/*
 * -------------------------------------------------------------------------
 * RETURN (15+)
 * -------------------------------------------------------------------------
 */

const returnRules: Rule[] = [
  r({
    id: 'return.splitstep.absent',
    category: 'return',
    conditionDescription: 'No split-step on return.',
    condition: NEVER,
    feedbackPt:
      'Faça split-step no momento em que o adversário bate o saque. Sem ele você está parado para reagir.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.9,
    requiredData: ['splitStepDetected'],
  }),
  r({
    id: 'return.swing.too-long',
    category: 'return',
    conditionDescription: 'Return swing > 700 ms vs first-serve speed.',
    condition: (s) => above(s.swingDurationMs, 700),
    feedbackPt:
      'Devolução com armada muito longa. Reduza o backswing no return — compact, como voleio longo.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.7,
  }),
  r({
    id: 'return.position.too-deep-first',
    category: 'return',
    conditionDescription: 'Return position > 2 m behind baseline on first serve.',
    condition: NEVER,
    feedbackPt:
      'Você está recuando muito para o primeiro saque. Fique perto da linha de base para pressionar ou ao menos aparar.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.55,
    requiredData: ['returnPositionBehindBaselineMeters', 'serveSpeedKmH'],
  }),
  r({
    id: 'return.position.too-close-second',
    category: 'return',
    conditionDescription: 'Return position on baseline for slow second serve.',
    condition: NEVER,
    feedbackPt:
      'No segundo saque, avance para dentro da quadra para atacar — se ficar na linha de base você não pressiona.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.55,
    requiredData: ['returnPositionBehindBaselineMeters', 'isSecondServe'],
  }),
  r({
    id: 'return.contact.late',
    category: 'return',
    conditionDescription: 'Contact behind front hip on return.',
    condition: NEVER,
    feedbackPt:
      'Bola atrasada na devolução — movimente o ombro cedo. Menor armada permite impacto à frente.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.8,
    requiredData: ['contactRelativeToFrontHipMeters'],
  }),
  r({
    id: 'return.target.neutral-middle',
    category: 'return',
    conditionDescription: 'Return to middle on pressure serves — low percentage.',
    condition: NEVER,
    feedbackPt:
      'Em saque difícil, devolver pelo meio profundo é mais seguro do que tentar ângulo. Margem alta > beleza.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.6,
    requiredData: ['returnTargetZone'],
  }),
  r({
    id: 'return.wide.coverage',
    category: 'return',
    conditionDescription: 'Wide serve to deuce — not stepping across.',
    condition: NEVER,
    feedbackPt:
      'No saque aberto, dê um passo grande diagonal para a frente, não lateral. Cobre mais área.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.7,
    requiredData: ['serveDirection', 'firstStepDirection'],
  }),
  r({
    id: 'return.t.coverage',
    category: 'return',
    conditionDescription: 'T-serve return consistently floated.',
    condition: NEVER,
    feedbackPt:
      'Em saque no T, block return com punho firme e face neutra. Evite tentar girar a bola.',
    severity: 'tip',
    source: PTR_MANUAL,
    confidence: 0.6,
    requiredData: ['serveDirection', 'returnTrajectoryType'],
  }),
  r({
    id: 'return.grip.ready-neutral',
    category: 'return',
    conditionDescription: 'Ready grip in forehand or backhand (not neutral Continental/semi).',
    condition: NEVER,
    feedbackPt:
      'No return, fique com empunhadura neutra (Continental ou semi-Eastern) para trocar rápido.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.7,
    requiredData: ['readyGripIndex'],
  }),
  r({
    id: 'return.ready.racket-low',
    category: 'return',
    conditionDescription: 'Ready racket head below wrist.',
    condition: NEVER,
    feedbackPt:
      'Ponta da raquete apontando para cima e levemente à frente na posição de espera. Raquete baixa atrasa sua reação.',
    severity: 'tip',
    source: PTR_MANUAL,
    confidence: 0.7,
    requiredData: ['readyRacketHeightIndex'],
  }),
  r({
    id: 'return.weight.backfoot',
    category: 'return',
    conditionDescription: 'Weight on back foot at contact.',
    condition: NEVER,
    feedbackPt:
      'Transfira o peso para frente na devolução. Se cair para trás, a bola vai curta e alta.',
    severity: 'warning',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.75,
    requiredData: ['comVelocityAtContactForward'],
  }),
  r({
    id: 'return.block.vs-first-serve',
    category: 'return',
    conditionDescription: 'Full swing against 1st serves > 180 km/h-equivalent.',
    condition: NEVER,
    feedbackPt:
      'Contra primeiro saque forte, use block return (armada mínima). Você aproveita a velocidade do adversário.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.7,
    requiredData: ['serveSpeedKmH'],
  }),
  r({
    id: 'return.attack.vs-second-serve',
    category: 'return',
    conditionDescription: 'Passive block on weak 2nd serves (< 120 km/h equivalent).',
    condition: NEVER,
    feedbackPt:
      'No segundo saque fraco, ataque: avance e bata com intenção. É sua melhor chance de virar o ponto.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.65,
    requiredData: ['serveSpeedKmH', 'isSecondServe', 'returnShotIntensity'],
  }),
  r({
    id: 'return.head.still',
    category: 'return',
    conditionDescription: 'Head rotation > 15° before contact on return.',
    condition: NEVER,
    feedbackPt:
      'Cabeça saindo cedo na devolução. Mantenha os olhos na bola — decisões rápidas começam no foco visual.',
    severity: 'tip',
    source: GROPPEL_STROKES,
    confidence: 0.6,
    requiredData: ['headRotationBeforeContactDeg'],
  }),
  r({
    id: 'return.cadence.rush-2ndshot',
    category: 'return',
    conditionDescription: '+1 shot after return inconsistent.',
    condition: NEVER,
    feedbackPt:
      'Plane o "+1" depois do return — devolução+bola seguinte é um combo. Não só responda, decida para onde vai a próxima.',
    severity: 'info',
    source: LOEHR_MENTAL,
    confidence: 0.5,
    requiredData: ['plusOneShotConsistencyIndex'],
  }),
  r({
    id: 'return.elbow.late-prep',
    category: 'return',
    conditionDescription: 'Elbow still flexed past 90° during takeback on return.',
    condition: NEVER,
    feedbackPt:
      'Preparação atrasada na devolução. Gire o ombro assim que o adversário lançar a bola.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.65,
    requiredData: ['takebackElbowDeg'],
  }),
];

/*
 * -------------------------------------------------------------------------
 * NET / APPROACH / TACTICS (15+)
 * -------------------------------------------------------------------------
 */

const netRules: Rule[] = [
  r({
    id: 'net.approach.no-follow-in',
    category: 'net',
    conditionDescription: 'Approach shot without following into net.',
    condition: NEVER,
    feedbackPt:
      'Se você bateu um approach (bola curta), siga para a rede. Metade da quadra é pior do que qualquer das duas.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.9,
    requiredData: ['approachShotDetected', 'followedToNet'],
  }),
  r({
    id: 'net.approach.deep-or-angle',
    category: 'net',
    conditionDescription: 'Approach shot short in middle of court.',
    condition: NEVER,
    feedbackPt:
      'Approach pelo meio curto = presa fácil. Aprofunde (último terço) ou abra o ângulo para limitar a passing.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.75,
    requiredData: ['approachShotLandingZone'],
  }),
  r({
    id: 'net.approach.slice-low',
    category: 'net',
    conditionDescription: 'Hit topspin approach rather than slice.',
    condition: NEVER,
    feedbackPt:
      'No approach, slice baixo força o passador a levantar a bola. Topspin é ok em altura, mas slice é o mais seguro.',
    severity: 'info',
    source: USPTA_MANUAL,
    confidence: 0.6,
    requiredData: ['approachShotSpin'],
  }),
  r({
    id: 'net.position.first-volley-too-far',
    category: 'net',
    conditionDescription: 'First volley from outside service line.',
    condition: NEVER,
    feedbackPt:
      'Primeiro voleio longe da rede é normal — o importante é o split-step. Depois avance para rede para o próximo.',
    severity: 'info',
    source: PTR_MANUAL,
    confidence: 0.6,
    requiredData: ['firstVolleyDistanceFromNet'],
  }),
  r({
    id: 'net.position.second-volley-closer',
    category: 'net',
    conditionDescription: 'Second volley not closer to net than first.',
    condition: NEVER,
    feedbackPt:
      'Seu segundo voleio deveria ser mais perto da rede do que o primeiro. Avance após cada bola.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.75,
    requiredData: ['firstVolleyDistanceFromNet', 'secondVolleyDistanceFromNet'],
  }),
  r({
    id: 'net.poach.early-move',
    category: 'net',
    conditionDescription: 'Poach decision made too early (telegraphed).',
    condition: NEVER,
    feedbackPt:
      'No poach (cruzada no dupla), decida no último instante para não entregar a jogada ao adversário.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.65,
    requiredData: ['poachLatencyMs'],
  }),
  r({
    id: 'net.lob.defensive-height',
    category: 'net',
    conditionDescription: 'Defensive lob below 6m peak height.',
    condition: NEVER,
    feedbackPt:
      'Lob defensivo precisa de altura (≥6m) e profundidade. Baixo vira smash fácil.',
    severity: 'warning',
    source: ITF_CSSR,
    confidence: 0.75,
    requiredData: ['lobPeakHeightMeters'],
  }),
  r({
    id: 'net.lob.offensive-topspin',
    category: 'net',
    conditionDescription: 'Offensive lob without topspin.',
    condition: NEVER,
    feedbackPt:
      'No lob ofensivo, use top spin — bola passa sobre o adversário e cai rápido.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.7,
    requiredData: ['lobTopspinRpm'],
  }),
  r({
    id: 'net.drop.target-close-net',
    category: 'net',
    conditionDescription: 'Drop shot lands too deep in service box.',
    condition: NEVER,
    feedbackPt:
      'Drop shot deve cair perto da rede (primeiro metro do quadrado de saque). Drop profundo é smash do adversário.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.8,
    requiredData: ['dropShotLandingDistanceFromNet'],
  }),
  r({
    id: 'net.drop.from-deep',
    category: 'net',
    conditionDescription: 'Drop shot attempted from behind baseline.',
    condition: NEVER,
    feedbackPt:
      'Drop shot de trás da linha de base é muito arriscado. Use só quando estiver dentro da quadra e o adversário recuado.',
    severity: 'warning',
    source: ITF_CSSR,
    confidence: 0.8,
    requiredData: ['dropShotContactPosition'],
  }),
  r({
    id: 'net.swing-volley.appropriate',
    category: 'net',
    conditionDescription: 'Swing-volley attempted on low floating ball.',
    condition: NEVER,
    feedbackPt:
      'Swing-volley só em bola alta flutuante. Em bola baixa, voleio normal com face aberta.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.8,
    requiredData: ['swingVolleyContactHeight'],
  }),
  r({
    id: 'net.doubles.i-formation-unused',
    category: 'net',
    conditionDescription: 'Never using I-formation on second serve returns.',
    condition: NEVER,
    feedbackPt:
      'Na dupla, varie a formação (ex: I-formation) em pontos-chave para confundir o retorno.',
    severity: 'info',
    source: PTR_MANUAL,
    confidence: 0.5,
    requiredData: ['doublesFormationHistogram'],
  }),
  r({
    id: 'net.pressure.volley-percentage',
    category: 'net',
    conditionDescription: 'Volley winning % < 50% — net game inefficient.',
    condition: NEVER,
    feedbackPt:
      'Sua taxa de vitória na rede está baixa. Trabalhe approach deep e split-step antes de cada voleio.',
    severity: 'warning',
    source: ITF_CSSR,
    confidence: 0.65,
    requiredData: ['volleyWinPercentage'],
  }),
  r({
    id: 'net.transition.hesitation',
    category: 'net',
    conditionDescription: 'Hesitant transition — stops at service line.',
    condition: NEVER,
    feedbackPt:
      'Não pare na linha de serviço (zona morta). Ou vai para a rede, ou recua para linha de base.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.8,
    requiredData: ['positionStaticTimeInDeadZoneMs'],
  }),
  r({
    id: 'net.passing.down-the-line',
    category: 'net',
    conditionDescription: 'Never using DTL passing shot.',
    condition: NEVER,
    feedbackPt:
      'Varie passing shots — paralelo (DTL) obriga o adversário a cobrir os dois lados.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.55,
    requiredData: ['passingShotDirectionHistogram'],
  }),
];

/*
 * -------------------------------------------------------------------------
 * MOVEMENT (15+)
 * -------------------------------------------------------------------------
 */

const movementRules: Rule[] = [
  r({
    id: 'movement.splitstep.consistent',
    category: 'movement',
    conditionDescription: 'Split-step frequency < 70% of opponent contacts.',
    condition: NEVER,
    feedbackPt:
      'Seu split-step não é constante. Cada bola do adversário = um split-step seu. É o principal hábito de movimentação.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.9,
    requiredData: ['splitStepFrequency'],
  }),
  r({
    id: 'movement.recovery.off-center',
    category: 'movement',
    conditionDescription: 'Recovery position > 1.5 m off the geometric center of opponent angles.',
    condition: NEVER,
    feedbackPt:
      'Depois de cada bola, recupere para o centro geométrico dos ângulos possíveis do adversário — nem sempre é o meio da linha de base.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.65,
    requiredData: ['recoveryCenterOffsetMeters'],
  }),
  r({
    id: 'movement.crossover.forbidden',
    category: 'movement',
    conditionDescription: 'Cross-over step used for short lateral distance.',
    condition: NEVER,
    feedbackPt:
      'Em distância curta lateral, use side-steps (shuffle), não cruzado. Cruzado é para longas distâncias.',
    severity: 'tip',
    source: USPTA_MANUAL,
    confidence: 0.75,
    requiredData: ['lateralStepType', 'lateralStepDistanceMeters'],
  }),
  r({
    id: 'movement.sideways.wide-balls',
    category: 'movement',
    conditionDescription: 'Short side-steps used on wide ball > 4m lateral.',
    condition: NEVER,
    feedbackPt:
      'Em bola muito aberta, use passos cruzados (cross-over) — side-steps são lentos demais.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.8,
    requiredData: ['lateralStepType', 'lateralStepDistanceMeters'],
  }),
  r({
    id: 'movement.recovery.first-step',
    category: 'movement',
    conditionDescription: 'First recovery step < 300 ms after contact.',
    condition: NEVER,
    feedbackPt:
      'O primeiro passo de recuperação deve sair imediatamente após o follow-through. Não admire a bola.',
    severity: 'tip',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.75,
    requiredData: ['firstRecoveryStepLatencyMs'],
  }),
  r({
    id: 'movement.anticipation.late',
    category: 'movement',
    conditionDescription: 'First step after opponent contact > 250 ms.',
    condition: NEVER,
    feedbackPt:
      'Sua reação está lenta. Antecipe lendo a postura do adversário — primeiros 200ms após o impacto dele são cruciais.',
    severity: 'warning',
    source: ITF_CSSR,
    confidence: 0.75,
    requiredData: ['anticipationLatencyMs'],
  }),
  r({
    id: 'movement.balance.crossover-during-hit',
    category: 'movement',
    conditionDescription: 'Feet crossing during swing.',
    condition: NEVER,
    feedbackPt:
      'Seus pés estão cruzados no impacto — equilíbrio ruim, controle ruim. Base aberta ou fechada, mas nunca cruzada.',
    severity: 'warning',
    source: PTR_MANUAL,
    confidence: 0.85,
    requiredData: ['feetCrossedAtContact'],
  }),
  r({
    id: 'movement.loadunload.weight-transfer',
    category: 'movement',
    conditionDescription: 'Weight not transferring forward at contact.',
    condition: NEVER,
    feedbackPt:
      'Transferência de peso fraca. "Carregue" na perna de trás e "descarregue" para a frente no impacto.',
    severity: 'warning',
    source: BAHAMONDE_KNUDSON_SERVE,
    confidence: 0.75,
    requiredData: ['comVelocityAtContactForward'],
  }),
  r({
    id: 'movement.hopping.excess',
    category: 'movement',
    conditionDescription: 'Vertical COM oscillation > 20cm while in "ready".',
    condition: NEVER,
    feedbackPt:
      'Você pula muito na espera. Fique com joelhos flexionados e peso nas pontas dos pés — quase estático, só ativo.',
    severity: 'tip',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.55,
    requiredData: ['readyStateComOscillation'],
  }),
  r({
    id: 'movement.backpedal.vs-turn-run',
    category: 'movement',
    conditionDescription: 'Back-pedaling to cover lob instead of turn-and-run.',
    condition: NEVER,
    feedbackPt:
      'Para cobrir lob, gire e corra — nunca recue de costas. Mais rápido e evita queda.',
    severity: 'warning',
    source: USPTA_MANUAL,
    confidence: 0.9,
    requiredData: ['backpedalDetected'],
  }),
  r({
    id: 'movement.sliding.hardcourt',
    category: 'movement',
    conditionDescription: 'Sliding on hard court risks injury.',
    condition: NEVER,
    feedbackPt:
      'Evite deslizar em quadra rápida (piso duro). Faça parada com passo de freio ("brake step").',
    severity: 'tip',
    source: KIBLER_CHANDLER_SHOULDER,
    confidence: 0.7,
    requiredData: ['courtSurface', 'slideDetected'],
  }),
  r({
    id: 'movement.sliding.clay-ok',
    category: 'movement',
    conditionDescription: 'No sliding used on clay.',
    condition: NEVER,
    feedbackPt:
      'Em saibro, aprenda a deslizar — economiza passo e mantém equilíbrio em bolas abertas.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.55,
    requiredData: ['courtSurface', 'slideDetected'],
  }),
  r({
    id: 'movement.stop.brake-step',
    category: 'movement',
    conditionDescription: 'No brake step before change of direction.',
    condition: NEVER,
    feedbackPt:
      'Antes de trocar de direção, frene com um passo de freio com a perna externa. Sem ele, você escorrega ou tropeça.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.75,
    requiredData: ['brakeStepDetected'],
  }),
  r({
    id: 'movement.court.deep-recovery',
    category: 'movement',
    conditionDescription: 'Recovering to baseline (flat), not 1m behind for depth balls.',
    condition: NEVER,
    feedbackPt:
      'Em bola profunda recupere 1m atrás da linha de base — se ficar na linha, bola profunda te obriga a bater de frente.',
    severity: 'tip',
    source: ITF_CSSR,
    confidence: 0.6,
    requiredData: ['recoveryDepthOffsetMeters'],
  }),
  r({
    id: 'movement.turn.unit-turn-late',
    category: 'movement',
    conditionDescription: 'Unit turn (shoulders) initiated after bounce.',
    condition: NEVER,
    feedbackPt:
      'O giro do tronco (unit turn) começa quando o adversário ainda está batendo, não depois que a bola quica no seu lado.',
    severity: 'warning',
    source: LANDLINGER_LIPTAK_FOOTWORK,
    confidence: 0.85,
    requiredData: ['unitTurnLatencyMs'],
  }),
];

/*
 * -------------------------------------------------------------------------
 * MENTAL (10+)
 * -------------------------------------------------------------------------
 * These are mostly informational — we cap confidence, and most require
 * aggregate data we don't yet capture.
 */

const mentalRules: Rule[] = [
  r({
    id: 'mental.ritual.between-points',
    category: 'mental',
    conditionDescription: 'Between-point rituals missing (immediate serve after prev point).',
    condition: NEVER,
    feedbackPt:
      'Use os 25 segundos entre pontos: respire, vire para o fundo, planeje o próximo ponto. Rituais reduzem ansiedade.',
    severity: 'tip',
    source: LOEHR_MENTAL,
    confidence: 0.65,
    requiredData: ['timeBetweenPointsSec'],
  }),
  r({
    id: 'mental.breathing.shallow',
    category: 'mental',
    conditionDescription: 'Breath-holding patterns detected at pressure points.',
    condition: NEVER,
    feedbackPt:
      'Expire no impacto — bloquear a respiração aumenta tensão muscular e erra.',
    severity: 'tip',
    source: WEINBERG_MENTAL,
    confidence: 0.5,
    requiredData: ['breathHoldIndex'],
  }),
  r({
    id: 'mental.selftalk.negative',
    category: 'mental',
    conditionDescription: 'Visible negative self-talk / head drops > 3× per set.',
    condition: NEVER,
    feedbackPt:
      'Cabeça baixa entre pontos é sinal vermelho. Andar de cabeça erguida, mesmo perdendo, afeta seu jogo.',
    severity: 'warning',
    source: LOEHR_MENTAL,
    confidence: 0.65,
    requiredData: ['negativeBodyLanguageIndex'],
  }),
  r({
    id: 'mental.routine.bounce-consistency',
    category: 'mental',
    conditionDescription: 'Ball-bounce count varies >1 between serves.',
    condition: NEVER,
    feedbackPt:
      'Mantenha o mesmo número de quiques antes de cada saque — consistência de rituais = consistência de execução.',
    severity: 'info',
    source: LOEHR_MENTAL,
    confidence: 0.6,
    requiredData: ['preServeBounceVariance'],
  }),
  r({
    id: 'mental.error.repeat-same',
    category: 'mental',
    conditionDescription: 'Same unforced error repeated > 3 times in set.',
    condition: NEVER,
    feedbackPt:
      'Você repetiu o mesmo erro mais de 3 vezes. Ajuste algo (alvo, altura, rotação) — não insista no que não funciona.',
    severity: 'warning',
    source: WEINBERG_MENTAL,
    confidence: 0.6,
    requiredData: ['unforcedErrorRepetitionIndex'],
  }),
  r({
    id: 'mental.bigpoints.patterns',
    category: 'mental',
    conditionDescription: 'Big points (30-30, breakpoints) — plays below set average.',
    condition: NEVER,
    feedbackPt:
      'Em pontos-chave você está jogando abaixo da média. Tenha um "Plano A" simples para pontos importantes — não tente jogada nova.',
    severity: 'tip',
    source: WEINBERG_MENTAL,
    confidence: 0.55,
    requiredData: ['bigPointPerformanceIndex'],
  }),
  r({
    id: 'mental.score.recovery-after-break',
    category: 'mental',
    conditionDescription: 'Often loses first point after breaking serve.',
    condition: NEVER,
    feedbackPt:
      'Muito cuidado após quebrar o saque — seu adversário fica mais solto. Comece firme, jogue uma bola dentro.',
    severity: 'tip',
    source: WEINBERG_MENTAL,
    confidence: 0.55,
    requiredData: ['firstPointAfterBreakIndex'],
  }),
  r({
    id: 'mental.variety.same-pattern',
    category: 'mental',
    conditionDescription: 'Over 70% of serves to same box location in a set.',
    condition: NEVER,
    feedbackPt:
      'Varie direções do saque — se mandar 70% para o mesmo lado o adversário já está esperando.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.6,
    requiredData: ['serveDirectionHistogram'],
  }),
  r({
    id: 'mental.focus.fatigue-late-set',
    category: 'mental',
    conditionDescription: 'Unforced error rate rises > 30% after 45 min.',
    condition: NEVER,
    feedbackPt:
      'Aumentou erros não-forçados na segunda metade — possível fadiga mental. Use ritual mais longo entre pontos.',
    severity: 'tip',
    source: LOEHR_MENTAL,
    confidence: 0.5,
    requiredData: ['unforcedErrorRateByTimeBucket'],
  }),
  r({
    id: 'mental.momentum.two-consecutive-errors',
    category: 'mental',
    conditionDescription: 'Two consecutive unforced errors on own serve.',
    condition: NEVER,
    feedbackPt:
      'Duas UEs seguidas no seu saque — quebre o momentum: respire fundo, ajuste toalha, bola dentro com rotação.',
    severity: 'tip',
    source: WEINBERG_MENTAL,
    confidence: 0.55,
    requiredData: ['consecutiveUnforcedErrorDetected'],
  }),
  r({
    id: 'mental.timeout.use-service-clock',
    category: 'mental',
    conditionDescription: 'Serving too fast — no recovery between points.',
    condition: NEVER,
    feedbackPt:
      'Você está sacando rápido demais (< 10 segundos). Use o tempo disponível para recuperar e pensar.',
    severity: 'tip',
    source: LOEHR_MENTAL,
    confidence: 0.55,
    requiredData: ['timeBetweenPointsSec'],
  }),
  r({
    id: 'session.volume.low',
    category: 'mental',
    conditionDescription: 'Session has fewer than 10 swings — insufficient data for reliable coaching.',
    condition: (_s, a) => a.sessionSwingCount < 10,
    feedbackPt:
      'Sessão curta — amostra pequena pode enganar. Grave pelo menos 20 golpes para conclusões mais confiáveis.',
    severity: 'info',
    source: ITF_CSSR,
    confidence: 0.6,
  }),
  r({
    id: 'session.consistency.velocity-low',
    category: 'mental',
    conditionDescription: 'Session median wrist ang. vel. < 7 rad/s across >= 10 swings.',
    condition: (_s, a) => a.sessionSwingCount >= 10 && a.medianPeakWristAngVel < 7,
    feedbackPt:
      'Velocidade angular mediana baixa na sessão — golpes estão pouco acelerados. Considere aquecimento + solte o antebraço.',
    severity: 'tip',
    source: ELLIOTT_REID_CRESPO_ADVANCED_TENNIS,
    confidence: 0.5,
  }),
];

/*
 * -------------------------------------------------------------------------
 * Root-cause linkage table
 * -------------------------------------------------------------------------
 * Rather than clutter each rule definition with causal metadata, we attach
 * `causes`, `implies`, and `weight` here in a flat table. The final
 * `rules` export merges each base rule with its link entry. This keeps
 * the biomechanics text easy to scan while giving the causal-graph engine
 * (causalGraph.ts) enough structure to aggregate symptoms into upstream
 * faults.
 *
 * Weights are in [0, 1] and represent how strongly a fired symptom argues
 * for a given cause. A symptom may point at multiple causes (e.g. a low
 * wrist velocity on a serve can be both `arm-only-swing` AND `no-pronation`).
 *
 * Cause IDs must match those in `rootCauses.ts`. An integrity test enforces
 * this at load time.
 */

interface RuleLink {
  readonly causes?: readonly string[];
  readonly implies?: readonly string[];
  readonly weight?: number;
}

const ruleLinks: Readonly<Record<string, RuleLink>> = Object.freeze({
  // --- SERVE ---
  'serve.contact.elbow-too-bent': {
    causes: ['cramped-contact', 'arm-only-swing'],
    implies: ['serve.power.low-wrist-velocity'],
    weight: 0.55,
  },
  'serve.contact.elbow-locked': {
    causes: ['locked-arm-contact'],
    weight: 0.7,
  },
  'serve.power.low-wrist-velocity': {
    causes: ['no-pronation', 'arm-only-swing'],
    implies: ['serve.pronation.missing'],
    weight: 0.6,
  },
  'serve.tempo.swing-too-short': {
    causes: ['rushed-tempo'],
    weight: 0.45,
  },
  'serve.tempo.swing-too-long': {
    causes: ['late-preparation'],
    weight: 0.4,
  },
  'serve.consistency.velocity-vs-median': {
    causes: ['session-fatigue'],
    weight: 0.35,
  },
  'serve.stance.no-leg-drive': {
    causes: ['weak-leg-drive', 'arm-only-swing', 'poor-weight-transfer'],
    weight: 0.7,
  },
  'serve.stance.insufficient-knee-bend': {
    causes: ['weak-leg-drive', 'insufficient-knee-flexion'],
    weight: 0.6,
  },
  'serve.trophy.no-shoulder-shoulder': {
    causes: ['poor-trunk-rotation'],
    weight: 0.5,
  },
  'serve.rotation.no-trunk-rotation': {
    causes: ['poor-trunk-rotation', 'arm-only-swing'],
    weight: 0.7,
  },
  'serve.followthrough.abbreviated': {
    causes: ['truncated-followthrough'],
    weight: 0.55,
  },
  'serve.grip.frying-pan': {
    causes: ['no-pronation'],
    implies: ['serve.pronation.missing'],
    weight: 0.75,
  },
  'serve.pronation.missing': {
    causes: ['no-pronation'],
    weight: 0.85,
  },
  'serve.chain.hip-shoulder-separation': {
    causes: ['poor-trunk-rotation', 'arm-only-swing'],
    weight: 0.6,
  },
  'serve.spin.flat-second': {
    causes: ['no-pronation'],
    weight: 0.4,
  },
  'serve.ritual.no-pre-serve-pattern': {
    causes: ['mental-pressure-leak'],
    weight: 0.35,
  },
  'serve.doublefault.high-rate': {
    causes: ['mental-pressure-leak', 'rushed-tempo'],
    weight: 0.4,
  },

  // --- FOREHAND ---
  'forehand.contact.elbow-too-straight': {
    causes: ['locked-arm-contact'],
    weight: 0.55,
  },
  'forehand.contact.elbow-too-bent': {
    causes: ['cramped-contact', 'late-preparation'],
    implies: ['forehand.contact.late-contact'],
    weight: 0.6,
  },
  'forehand.power.low-wrist-velocity': {
    causes: ['arm-only-swing', 'poor-trunk-rotation'],
    weight: 0.5,
  },
  'forehand.tempo.rushed-swing': {
    causes: ['rushed-tempo', 'late-preparation'],
    weight: 0.5,
  },
  'forehand.tempo.long-swing': {
    causes: ['late-preparation'],
    weight: 0.3,
  },
  'forehand.consistency.elbow-variability': {
    causes: ['session-fatigue'],
    weight: 0.25,
  },
  'forehand.stance.open-stance-recovery': {
    causes: ['poor-weight-transfer'],
    weight: 0.5,
  },
  'forehand.topspin.low-racket-drop': {
    causes: ['grip-too-continental-for-fh'],
    weight: 0.4,
  },
  'forehand.kinetic.no-hip-rotation': {
    causes: ['poor-trunk-rotation', 'arm-only-swing'],
    weight: 0.75,
  },
  'forehand.kinetic.shoulder-lag': {
    causes: ['poor-trunk-rotation'],
    weight: 0.5,
  },
  'forehand.contact.late-contact': {
    causes: ['late-contact-point', 'late-preparation'],
    implies: ['forehand.tempo.rushed-swing'],
    weight: 0.8,
  },
  'forehand.contact.reach-contact': {
    causes: ['late-preparation', 'poor-recovery'],
    weight: 0.5,
  },
  'forehand.grip.western-on-low-ball': {
    causes: ['western-grip-on-low-balls'],
    weight: 0.7,
  },
  'forehand.followthrough.truncated': {
    causes: ['truncated-followthrough'],
    weight: 0.6,
  },
  'forehand.footwork.no-splitstep': {
    causes: ['no-splitstep'],
    weight: 0.8,
  },
  'forehand.footwork.static-feet': {
    causes: ['no-splitstep', 'poor-recovery'],
    weight: 0.5,
  },
  'forehand.head.eyes-off-ball': {
    causes: ['eye-lift-at-contact'],
    weight: 0.7,
  },
  'forehand.shoulder.no-turn': {
    causes: ['no-unit-turn', 'poor-trunk-rotation'],
    weight: 0.75,
  },
  'forehand.height.low-ball-no-bend': {
    causes: ['insufficient-knee-flexion', 'western-grip-on-low-balls'],
    weight: 0.55,
  },
  'forehand.balance.falling-back': {
    causes: ['poor-weight-transfer', 'late-contact-point'],
    weight: 0.55,
  },
  'forehand.recovery.no-recovery-step': {
    causes: ['poor-recovery'],
    weight: 0.7,
  },
  'forehand.wrist.breaking-down': {
    causes: ['wristy-swing', 'grip-too-continental-for-fh'],
    weight: 0.55,
  },

  // --- BACKHAND ---
  'backhand.contact.elbow-too-bent': {
    causes: ['cramped-contact', 'late-preparation'],
    weight: 0.55,
  },
  'backhand.contact.elbow-too-straight': {
    causes: ['locked-arm-contact'],
    weight: 0.55,
  },
  'backhand.power.low-wrist-velocity': {
    causes: ['arm-only-swing'],
    weight: 0.45,
  },
  'backhand.tempo.too-fast': {
    causes: ['rushed-tempo'],
    weight: 0.5,
  },
  'backhand.shoulder.insufficient-turn': {
    causes: ['no-unit-turn', 'poor-trunk-rotation'],
    weight: 0.7,
  },
  'backhand.contact.behind-front-hip': {
    causes: ['late-contact-point', 'late-preparation'],
    weight: 0.75,
  },
  'backhand.wrist.too-loose': {
    causes: ['wristy-swing'],
    weight: 0.6,
  },
  'backhand.followthrough.across-body': {
    causes: ['truncated-followthrough'],
    weight: 0.35,
  },
  'backhand.takeback.too-high': {
    causes: ['late-preparation'],
    weight: 0.35,
  },
  'backhand.kinetic.legs-quiet': {
    causes: ['weak-leg-drive', 'arm-only-swing', 'poor-weight-transfer'],
    weight: 0.6,
  },
  'backhand.head.spinout': {
    causes: ['eye-lift-at-contact'],
    weight: 0.65,
  },
  'backhand.contact.too-close-body': {
    causes: ['cramped-contact'],
    weight: 0.5,
  },
  'backhand.consistency.fatigue-late-session': {
    causes: ['session-fatigue'],
    weight: 0.75,
  },

  // --- VOLLEY ---
  'volley.swing.too-long': {
    causes: ['rushed-tempo'],
    weight: 0.3,
  },
  'volley.elbow.too-bent': {
    causes: ['cramped-contact'],
    weight: 0.5,
  },
  'volley.elbow.too-straight': {
    causes: ['locked-arm-contact'],
    weight: 0.5,
  },
  'volley.wrist.wristy': {
    causes: ['wristy-swing'],
    weight: 0.65,
  },
  'volley.stance.not-stepping-in': {
    causes: ['poor-weight-transfer'],
    weight: 0.6,
  },
  'volley.lowvolley.no-knee-bend': {
    causes: ['insufficient-knee-flexion', 'weak-leg-drive'],
    weight: 0.65,
  },
  'volley.splitstep.timing': {
    causes: ['no-splitstep'],
    weight: 0.7,
  },

  // --- OVERHEAD ---
  'overhead.shoulder.no-turn': {
    causes: ['no-unit-turn', 'poor-trunk-rotation'],
    weight: 0.6,
  },
  'overhead.elbow.too-bent': {
    causes: ['cramped-contact'],
    weight: 0.4,
  },

  // --- RETURN ---
  'return.splitstep.absent': {
    causes: ['no-splitstep'],
    weight: 0.8,
  },
  'return.contact.late': {
    causes: ['late-contact-point', 'late-preparation'],
    weight: 0.75,
  },
  'return.head.still': {
    causes: ['eye-lift-at-contact'],
    weight: 0.5,
  },
  'return.elbow.late-prep': {
    causes: ['late-preparation', 'no-unit-turn'],
    weight: 0.6,
  },
  'return.swing.too-long': {
    causes: ['rushed-tempo'],
    weight: 0.3,
  },

  // --- MOVEMENT ---
  'movement.splitstep.consistent': {
    causes: ['no-splitstep'],
    weight: 0.5,
  },
  'movement.recovery.off-center': {
    causes: ['poor-recovery'],
    weight: 0.7,
  },
  'movement.recovery.first-step': {
    causes: ['poor-recovery'],
    weight: 0.6,
  },
  'movement.turn.unit-turn-late': {
    causes: ['no-unit-turn', 'late-preparation'],
    weight: 0.7,
  },
  'movement.loadunload.weight-transfer': {
    causes: ['poor-weight-transfer', 'weak-leg-drive'],
    weight: 0.55,
  },
  'movement.court.deep-recovery': {
    causes: ['poor-recovery'],
    weight: 0.4,
  },

  // --- MENTAL / SESSION ---
  'mental.breathing.shallow': {
    causes: ['mental-pressure-leak', 'rushed-tempo'],
    weight: 0.5,
  },
  'mental.selftalk.negative': {
    causes: ['mental-pressure-leak'],
    weight: 0.6,
  },
  'mental.momentum.two-consecutive-errors': {
    causes: ['mental-pressure-leak'],
    weight: 0.65,
  },
  'mental.ritual.between-points': {
    causes: ['mental-pressure-leak'],
    weight: 0.4,
  },
  'mental.bigpoints.patterns': {
    causes: ['mental-pressure-leak'],
    weight: 0.55,
  },
  'mental.focus.fatigue-late-set': {
    causes: ['session-fatigue', 'mental-pressure-leak'],
    weight: 0.55,
  },
  'mental.error.repeat-same': {
    causes: ['mental-pressure-leak'],
    weight: 0.3,
  },
  'session.consistency.velocity-low': {
    causes: ['session-fatigue', 'arm-only-swing'],
    weight: 0.4,
  },
});

/**
 * Merge a base rule with its optional link entry. Preserves immutability.
 */
function withLinks(rule: Rule): Rule {
  const link = ruleLinks[rule.id];
  if (!link) return rule;
  return Object.freeze({
    ...rule,
    ...(link.causes ? { causes: Object.freeze([...link.causes]) } : {}),
    ...(link.implies ? { implies: Object.freeze([...link.implies]) } : {}),
    ...(link.weight !== undefined ? { weight: link.weight } : {}),
  });
}

/*
 * -------------------------------------------------------------------------
 * Final frozen rulebook
 * -------------------------------------------------------------------------
 */

export const rules: readonly Rule[] = Object.freeze(
  [
    ...serveRules,
    ...forehandRules,
    ...backhandRules,
    ...volleyRules,
    ...returnRules,
    ...netRules,
    ...movementRules,
    ...mentalRules,
  ].map(withLinks),
);
